import * as constants from '../constants/calculatorConstants'
import { getBaselineExcess, getFloorProperties } from '../adapters/baselineLoadAdapter'
import {
    DU_DEFAULT_MILLICORES_PER_POD,
    DU_TRAINING_CPU_MILLICORES_PER_POD,
    DU_TRAINING_RAM_MIB_PER_POD,
    DU_DIGITIZER_PAGES_PER_HOUR_PER_POD,
    DU_DIGITIZER_CPU_MILLICORES_PER_POD,
    DU_DIGITIZER_RAM_MIB_PER_POD,
    DU_FRAMEWORK_PAGES_PER_HOUR_PER_POD,
    DU_FRAMEWORK_RAM_MIB_PER_POD,
    DU_CLASSIFIER_PAGES_PER_HOUR_PER_POD,
    DU_CLASSIFIER_RAM_MIB_PER_POD,
    DU_CUSTOM_CLASSIFIER_PAGES_PER_HOUR_PER_POD,
    DU_CUSTOM_CLASSIFIER_CPU_MILLICORES_PER_POD,
    DU_CUSTOM_CLASSIFIER_RAM_MIB_PER_POD,
    DU_OOB_EXTRACTOR_PAGES_PER_HOUR_GPU_PER_POD,
    DU_OOB_EXTRACTOR_RAM_MIB_PER_POD,
    DU_CUSTOM_EXTRACTOR_PAGES_PER_HOUR_GPU_PER_POD,
    DU_CUSTOM_EXTRACTOR_RAM_MIB_PER_POD,
    DU_OCR_PAGES_PER_HOUR_PER_POD,
    DU_OCR_RAM_MIB_PER_POD,
    DU_CPU_INFERENCE_SLOWDOWN_FACTOR,
    DU_EXTENDED_OCR_CPU_MILLICORES_PER_POD,
    DU_EXTENDED_OCR_RAM_MIB_PER_POD,
} from '../constants/duModernProjectsConstants'
import { Profile } from './calculationUtils';

// Uniform context every product handler receives as its final argument —
// handlers pick what they need; no per-product dispatch branching.
export interface ProductHandlerContext {
    profile?: Profile;
    services?: string[];
    baselineProfile?: string;
}

export function formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function formatCpu(cpu: number, decimals = 2) {
    if (cpu === 0) return '0 vCPU';
    const dm = decimals < 0 ? 0 : decimals;
    return parseFloat((cpu / 1000.0).toFixed(dm)) + " vCPU";
}

/**
 * Round a CPU value (in millicores) to 2 decimals of vCPU (i.e. to the nearest 0.01 vCPU
 * = 10 millicores). Keeps every intermediate and final CPU total at 2-decimal precision.
 */
export function round2Cpu(millicores: number): number {
    return Math.round(millicores / 10) * 10;
}

/**
 * Round a RAM value (in MiB) to 2 decimals of GiB (i.e. to the nearest 0.01 GiB).
 * Keeps every intermediate and final RAM total at 2-decimal precision.
 */
export function round2Ram(mib: number): number {
    return (Math.round((mib / 1024) * 100) / 100) * 1024;
}

export function getMultizonalFactor(nodes: number) {
    let factor = 1.5;
    if (nodes % 3 === 1) {
        factor = nodes/(2*(Math.floor(nodes/3)));
    }
    else if (nodes % 3 === 2) {
        factor = 1.5 + 1/(2*(Math.ceil(nodes/3)-1));
    }

    return factor;
}

const additionalRequirements = (dimensionName: string, dimensionValue: number, cpu: number, ram: number): string => {
    if (dimensionValue === 0) {
        return "";
    }
    if (cpu === 0 && ram === 0) {
        return `* **${dimensionValue}** **${dimensionName}** can be handled by the minimum number of replicas.`;
    }
    return `* Handling **${dimensionValue}** of **${dimensionName}** requires an additional **${formatCpu(cpu, 2)}** / **${formatBytes(ram * 1024 * 1024, 2)}** (already included in the **Total** above).`;
};


function throwUnsupportedCoresError(): never {
    throw new Error("Cores on host value is not supported.");
}

export function calculateAllocatableCPU(coresOnHost: number): number {
    return coresOnHost < 1 ? coresOnHost - 60 / 1000 :
        coresOnHost <= 2 ? coresOnHost - 100 / 1000 :
            coresOnHost <= 4 ? coresOnHost - 140 / 1000 :
                coresOnHost <= 8 ? coresOnHost - 180 / 1000 :
                    coresOnHost <= 16 ? coresOnHost - 260 / 1000 :
                        coresOnHost <= 32 ? coresOnHost - 420 / 1000 :
                            coresOnHost <= 64 ? coresOnHost - 740 / 1000 :
                                throwUnsupportedCoresError();
}

export function calculateAllocatableMemory(memoryInBytes: number): number {
    const memoryInGB = memoryInBytes / (1024 * 1024 * 1024);
    const hardEvictionThreshold = 750 / 1024;
    const effectiveMemory = Math.max(memoryInGB - hardEvictionThreshold, 0);

    const reservedMemory = (
        (effectiveMemory <= 4 ? effectiveMemory * 0.25 :
            effectiveMemory <= 8 ? 4 * 0.25 + (effectiveMemory - 4) * 0.20 :
                effectiveMemory <= 16 ? 4 * 0.25 + 4 * 0.20 + (effectiveMemory - 8) * 0.10 :
                    effectiveMemory <= 128 ? 4 * 0.25 + 4 * 0.20 + 8 * 0.10 + (effectiveMemory - 16) * 0.06 :
                        4 * 0.25 + 4 * 0.20 + 8 * 0.10 + 112 * 0.06 + (effectiveMemory - 128) * 0.02) + hardEvictionThreshold
    );

    const allocatableMemoryInGB = memoryInGB - reservedMemory;
    return allocatableMemoryInGB * (1024 * 1024 * 1024);
}

export function processOrchestratorProduct(product : ProductOptionInterface, details : Array<string>, totalCpu : number, totalRam : number,) {
    if (product.properties === undefined) {
        return ;
    }

    let cpuDelta = 0;
    let ramDelta = 0;
    let concurrentRobots = +product.properties["concurrentRobots"];
    if (concurrentRobots <= 100) {
        // no-op
    } else if (concurrentRobots <= 1000) {
        cpuDelta += 4000;
        ramDelta += 8000;
    } else if (concurrentRobots <= 10000) {
        cpuDelta += 15000;
        ramDelta += 16000;
    } else {
        cpuDelta += 75000;
        ramDelta += 22000;
    }

    details.push(additionalRequirements("Robots", concurrentRobots, cpuDelta, ramDelta));
    totalCpu += cpuDelta;
    totalRam += ramDelta;

    return {totalCpu, totalRam, details, totalPvc: 0};
}

export function processPlatformProduct(product : ProductOptionInterface, details : Array<string>, totalCpu : number, totalRam : number,) {
    if (product.properties === undefined) {
        return ;
    }

    {
        let cpuDelta = 0;
        let ramDelta = 0;
        let loginPerSecond = +product.properties["loginPerSecond"];
        if (loginPerSecond <= 5) {
            // no-op
        } else if (loginPerSecond <= 20) {
            cpuDelta = 1000;
            ramDelta = 1000;
        } else if (loginPerSecond <= 50) {
            cpuDelta = 3000;
            ramDelta = 12000;
        } else if (loginPerSecond <= 100) {
            cpuDelta = 9000;
            ramDelta = 20000;
        } else {
            cpuDelta = 16000;
            ramDelta = 30000;
        }
        details.push(additionalRequirements("Logins Per Second", loginPerSecond, cpuDelta, ramDelta));
        totalCpu += cpuDelta;
        totalRam += ramDelta;
    }

    {
        let cpuDelta = 0;
        let ramDelta = 0;
        let concurrentUsers = +product.properties["concurrentUsers"];
        if (concurrentUsers <= 10) {
            // no-op
        } else if (concurrentUsers <= 25) {
            cpuDelta = 1000;
            ramDelta = 1500;
        } else if (concurrentUsers <= 50) {
            cpuDelta = 3000;
            ramDelta = 4500;
        } else if (concurrentUsers <= 100) {
            cpuDelta = 5000;
            ramDelta = 6500;
        } else if (concurrentUsers <= 150) {
            cpuDelta = 7000;
            ramDelta = 8500;
        } else {
            cpuDelta = 10000;
            ramDelta = 12000;
        }
        details.push(additionalRequirements("Concurrent Users", concurrentUsers, cpuDelta, ramDelta));
        totalCpu += cpuDelta;
        totalRam += ramDelta;
    }

    return { totalCpu, totalRam, details, totalPvc: 0 };
}

export function processAutomationOpsProduct(product : ProductOptionInterface, details : Array<string>, totalCpu : number, totalRam : number,) {
    if (product.properties === undefined) {
        return ;
    }

    let cpuDelta = 0;
    let ramDelta = 0;
    let concurrentUsers = +product.properties["concurrentUsers"];
    if (concurrentUsers <= 10) {
        // no-op
    } else if (concurrentUsers <= 55) {
        cpuDelta = 1500;
        ramDelta = 1500;
    } else {
        cpuDelta = 4000;
        ramDelta = 2000;
    }
    details.push(additionalRequirements("Concurrent Users", concurrentUsers, cpuDelta, ramDelta));
    totalCpu += cpuDelta;
    totalRam += ramDelta;

    return { totalCpu, totalRam, details, totalPvc: 0 };
}

export function processAutomationHubProduct(product : ProductOptionInterface, details : Array<string>, totalCpu : number, totalRam : number,) {
    if (product.properties === undefined) {
        return ;
    }

    let cpuDelta = 0;
    let ramDelta = 0;
    let concurrentUsers = +product.properties["concurrentUsers"];
    if (concurrentUsers <= 10) {
        // no-op
    } else if (concurrentUsers <= 20) {
        cpuDelta = 1000;
        ramDelta = 2000;
    } else {
        cpuDelta = 7000;
        ramDelta = 4000;
    }
    details.push(additionalRequirements("Concurrent Users", concurrentUsers, cpuDelta, ramDelta));
    totalCpu += cpuDelta;
    totalRam += ramDelta;

    return { totalCpu, totalRam, details, totalPvc: 0 };
}

const calculateDUModelRequirements = (modelVersion: string, pagesPerHour: number, processingTimeSeconds: number = 12) => {
    let cpuDelta, ramDelta;
    switch (modelVersion) {
      case "23.4":
        if (pagesPerHour <= 110) {
          cpuDelta = 1000;
        } else if (pagesPerHour <= 170) {
          cpuDelta = 2000;
        } else if (pagesPerHour <= 250) {
          cpuDelta = 4000;
        } else {
          cpuDelta = 6 * 1000;
        }
        ramDelta = 4 * 1024 * cpuDelta/1000;
        break;
      case "23.7":
      case "23.10":
        if (pagesPerHour <= 180) {
          cpuDelta = 1000;
        } else if (pagesPerHour <= 240) {
          cpuDelta = 2000;
        } else if (pagesPerHour <= 325) {
          cpuDelta = 4000;
        } else {
          cpuDelta = 6 * 1000;
        }
        ramDelta = 4 * 1024 * cpuDelta/1000;
        break;
      default:
        cpuDelta = 1000 * Math.ceil(processingTimeSeconds * pagesPerHour / 3600);
        ramDelta = 4 * 1024 * Math.ceil(processingTimeSeconds * pagesPerHour / 3600);
        break;
    }
    return { cpuDelta, ramDelta };
  };

/**
 * CPU (millicores) / RAM (MiB) footprint of the DU Modern Projects model-serving pods for a
 * given workload. Excludes the Extended OCR feature (handled separately). This is used both
 * for the actual workload and for the default workload, so only the increase above the
 * default is charged as "additional" (the default footprint is covered by the du-modern base).
 */
export function computeDuModernFootprint(
    customModelPagesPerHour: number,
    oobModelPagesPerHour: number,
    modelsTrainingInParallel: number,
    cpuInference: boolean
): { cpu: number; ram: number } {
    let cpu = 0;
    let ram = 0;

    // training cpu + training classifier
    const trainingReplicas = Math.max(1, modelsTrainingInParallel);
    cpu += Math.ceil(trainingReplicas * DU_TRAINING_CPU_MILLICORES_PER_POD);
    ram += trainingReplicas * DU_TRAINING_RAM_MIB_PER_POD;
    // digitizer cpu pages
    const digitizerReplicas = Math.ceil((customModelPagesPerHour + oobModelPagesPerHour) / DU_DIGITIZER_PAGES_PER_HOUR_PER_POD);
    cpu += digitizerReplicas * DU_DIGITIZER_CPU_MILLICORES_PER_POD;
    ram += digitizerReplicas * DU_DIGITIZER_RAM_MIB_PER_POD;
    // framework cpu
    const frameworkReplicas = Math.ceil((customModelPagesPerHour + oobModelPagesPerHour) / DU_FRAMEWORK_PAGES_PER_HOUR_PER_POD);
    cpu += frameworkReplicas * DU_DEFAULT_MILLICORES_PER_POD;
    ram += frameworkReplicas * DU_FRAMEWORK_RAM_MIB_PER_POD;
    // classifier cpu
    const classifierReplicas = Math.ceil(Math.max(1, oobModelPagesPerHour) / DU_CLASSIFIER_PAGES_PER_HOUR_PER_POD);
    cpu += classifierReplicas * DU_DEFAULT_MILLICORES_PER_POD;
    ram += classifierReplicas * DU_CLASSIFIER_RAM_MIB_PER_POD;
    // custom classifier cpu
    const customClassifierReplicas = Math.ceil(Math.max(1, customModelPagesPerHour) / DU_CUSTOM_CLASSIFIER_PAGES_PER_HOUR_PER_POD);
    cpu += customClassifierReplicas * DU_CUSTOM_CLASSIFIER_CPU_MILLICORES_PER_POD;
    ram += customClassifierReplicas * DU_CUSTOM_CLASSIFIER_RAM_MIB_PER_POD;

    // OOB extractor: 5x slower pages/hr on CPU.
    const oobReplicas = cpuInference
        ? Math.ceil(oobModelPagesPerHour / (DU_OOB_EXTRACTOR_PAGES_PER_HOUR_GPU_PER_POD / DU_CPU_INFERENCE_SLOWDOWN_FACTOR))
        : Math.ceil(oobModelPagesPerHour / DU_OOB_EXTRACTOR_PAGES_PER_HOUR_GPU_PER_POD);
    ram += oobReplicas * DU_OOB_EXTRACTOR_RAM_MIB_PER_POD;
    // Custom model extractor: 5x slower pages/hr on CPU.
    const customModelReplicas = cpuInference
        ? Math.max(1, Math.ceil(customModelPagesPerHour / (DU_CUSTOM_EXTRACTOR_PAGES_PER_HOUR_GPU_PER_POD / DU_CPU_INFERENCE_SLOWDOWN_FACTOR) + modelsTrainingInParallel))
        : Math.max(1, Math.ceil(customModelPagesPerHour / DU_CUSTOM_EXTRACTOR_PAGES_PER_HOUR_GPU_PER_POD + modelsTrainingInParallel));
    ram += customModelReplicas * DU_CUSTOM_EXTRACTOR_RAM_MIB_PER_POD;

    const ocrReplicas = Math.ceil((customModelPagesPerHour + oobModelPagesPerHour) / DU_OCR_PAGES_PER_HOUR_PER_POD);
    ram += ocrReplicas * DU_OCR_RAM_MIB_PER_POD;

    // Custom model training replicas (training RAM counted twice: CPU nodes + GPU nodes)
    ram += trainingReplicas * DU_TRAINING_RAM_MIB_PER_POD;

    // CPU inference: OOB extractor and custom extractor move to regular CPU nodes
    if (cpuInference) {
        cpu += oobReplicas * DU_DEFAULT_MILLICORES_PER_POD;
        cpu += customModelReplicas * DU_DEFAULT_MILLICORES_PER_POD;
    }

    return { cpu, ram };
}

export function processDocumentUnderstandingProduct(product : ProductOptionInterface, details : Array<string>, totalCpu : number, totalRam : number) {
    if (product.properties === undefined) {
        return ;
    }

    let modelVersion = product.properties["modelVersion"];

    {
        let generalExtractorPerHour = +product.properties["generalExtractorPerHour"];
        if (generalExtractorPerHour > 0) {
            const { cpuDelta, ramDelta } = calculateDUModelRequirements(modelVersion, generalExtractorPerHour);
            details.push(additionalRequirements("General Extractor (pages per hour)", generalExtractorPerHour, cpuDelta, ramDelta));
            totalCpu += cpuDelta;
            totalRam += ramDelta;
        }
    }

    {
        let invoicesPerHour = +product.properties["invoicesPerHour"];
        if (invoicesPerHour > 0) {
            const { cpuDelta, ramDelta } = calculateDUModelRequirements("", invoicesPerHour, 5);
            details.push(additionalRequirements("Invoices (pages per hour)", invoicesPerHour, cpuDelta, ramDelta));
            totalCpu += cpuDelta;
            totalRam += ramDelta;
        }
    }

    {
        let receiptsPerHour = +product.properties["receiptsPerHour"];
        if (receiptsPerHour > 0) {
            const { cpuDelta, ramDelta } = calculateDUModelRequirements(modelVersion, receiptsPerHour);
            details.push(additionalRequirements("Receipts (pages per hour)", receiptsPerHour, cpuDelta, ramDelta));
            totalCpu += cpuDelta;
            totalRam += ramDelta;
        }
    }

    {
        let purchaseOrdersPerHour = +product.properties["purchaseOrdersPerHour"];
        if (purchaseOrdersPerHour > 0) {
            const { cpuDelta, ramDelta } = calculateDUModelRequirements(modelVersion, purchaseOrdersPerHour);
            details.push(additionalRequirements("Purchase Order (pages per hour)", purchaseOrdersPerHour, cpuDelta, ramDelta));
            totalCpu += cpuDelta;
            totalRam += ramDelta;
        }
    }

    {
        let utilityBillsPerHour = +product.properties["utilityBillsPerHour"];
        if (utilityBillsPerHour > 0) {
            const { cpuDelta, ramDelta } = calculateDUModelRequirements(modelVersion, utilityBillsPerHour);
            details.push(additionalRequirements("Utility Bills (pages per hour)", utilityBillsPerHour, cpuDelta, ramDelta));
            totalCpu += cpuDelta;
            totalRam += ramDelta;
        }
    }

    {
        let ocrPerHour = +product.properties["ocrPerHour"];
        if (ocrPerHour > 0) {
            let cpuDelta = 4000 * Math.ceil(2 * ocrPerHour / 3600);
            let ramDelta = 8 * 1024 * Math.ceil(2 * ocrPerHour / 3600);
            details.push(additionalRequirements("OCR (pages per hour)", ocrPerHour, cpuDelta, ramDelta));
            totalCpu += cpuDelta;
            totalRam += ramDelta;
        }
    }

    {
        let shredsPerHour = +product.properties["shredsPerHour"];
        if (shredsPerHour > 0) {
            let cpuDelta = 1000 * Math.ceil(shredsPerHour / 1500.0);
            let ramDelta = shredsPerHour > 0 ? 6 * 1024 : 0;
            details.push(additionalRequirements("Intelligent Form Extractor (handwriting shreds per hour)", shredsPerHour, cpuDelta, ramDelta));
            totalCpu += cpuDelta;
            totalRam += ramDelta;
        }
    }

    {
        let isModernExperienceEnabled = !!product.properties["isModernProjectsEnabled"];
        if (isModernExperienceEnabled) {
            const customModelPagesPerHour = getNumericValue(product.properties["customModelPagesPerHour"]);
            const oobModelPagesPerHour = getNumericValue(product.properties["oobModelPagesPerHour"]);
            const modelsTrainingInParallel = getNumericValue(product.properties["modelsTrainingInParallel"]);
            const cpuInference = !!product.properties["cpuInference"];
            const extendedOcr = !!product.properties["extendedOcr"];

            // The du-modern base already covers the default workload. Charge only the increase
            // above the default inputs, so the "additional" line appears only when scaled up.
            // Numeric defaults live in baseline-load.json (the product constant keeps flags only).
            const dfltFlags = constants.DOCUMENT_UNDERSTANDING_MODERN_PROJECTS_PRODUCT_OPTION.properties!;
            const dflt = getFloorProperties(constants.DOCUMENT_UNDERSTANDING_MODERN_PROJECTS_PRODUCT_OPTION.key, 'default');
            const actual = computeDuModernFootprint(customModelPagesPerHour, oobModelPagesPerHour, modelsTrainingInParallel, cpuInference);
            const base = computeDuModernFootprint(
                getNumericValue(dflt["customModelPagesPerHour"]),
                getNumericValue(dflt["oobModelPagesPerHour"]),
                getNumericValue(dflt["modelsTrainingInParallel"]),
                !!dfltFlags["cpuInference"],
            );
            const cpuDelta = Math.max(0, actual.cpu - base.cpu);
            const ramDelta = Math.max(0, actual.ram - base.ram);

            if (cpuDelta > 0 || ramDelta > 0) {
                details.push(`* Handling **Custom Model Pages (per hour)** of **${customModelPagesPerHour}** and **OOB Model Pages (per hour)** of **${oobModelPagesPerHour}** requires an additional **${formatCpu(cpuDelta, 1)}** / **${formatBytes(ramDelta * 1024 * 1024, 1)}**.`);
                totalCpu += cpuDelta;
                totalRam += ramDelta;
            }

            // Extended OCR is a separate opt-in feature (default off), so it is always charged
            // and shown on its own line when enabled.
            if (extendedOcr) {
                const extendedOcrReplicas = Math.max(1, Math.ceil((oobModelPagesPerHour + customModelPagesPerHour) / DU_OCR_PAGES_PER_HOUR_PER_POD));
                const extendedOcrCpuDelta = extendedOcrReplicas * DU_EXTENDED_OCR_CPU_MILLICORES_PER_POD;
                const extendedOcrRamDelta = extendedOcrReplicas * DU_EXTENDED_OCR_RAM_MIB_PER_POD;

                details.push(additionalRequirements("Extended Languages OCR (pages per hour)", extendedOcrReplicas, extendedOcrCpuDelta, extendedOcrRamDelta));
                totalCpu += extendedOcrCpuDelta;
                totalRam += extendedOcrRamDelta;
            }
        }
    }
    return { totalCpu, totalRam, details, totalPvc: 0 };
}

export function processActionCenterProduct(product : ProductOptionInterface, details : Array<string>, totalCpu : number, totalRam : number) {
    if (product.properties === undefined) {
        return ;
    }

    let cpuDelta = 0;
    let ramDelta = 0;
    let concurrentUsers = +product.properties["concurrentUsers"];

    if (concurrentUsers <= 50) {
        // no-op
    } else if (concurrentUsers <= 100) {
        cpuDelta = 200;
        ramDelta = 500;
    } else if (concurrentUsers <= 500) {
        cpuDelta = 600;
        ramDelta = 1500;
    } else {
        cpuDelta = 1200;
        ramDelta = 3000;
    }

    totalCpu += cpuDelta;
    totalRam += ramDelta;

    details.push(additionalRequirements("Concurrent Users", concurrentUsers, cpuDelta, ramDelta));
    return { totalCpu, totalRam, details, totalPvc: 0};
}

export function processTestManagerProduct(product : ProductOptionInterface, details : Array<string>, totalCpu : number, totalRam : number) {
    if (product.properties === undefined) {
        return ;
    }

    let cpuDelta = 0;
    let ramDelta = 0;
    let concurrentUsers = +product.properties["concurrentUsers"];

    if (concurrentUsers <= 100) {
        // no-op
    } else if (concurrentUsers <= 300) {
        cpuDelta = 400;
        ramDelta = 900;
    } else {
        cpuDelta = 800;
        ramDelta = 1800;
    }

    details.push(additionalRequirements("Concurrent Users", concurrentUsers, cpuDelta, ramDelta));
    totalCpu += cpuDelta;
    totalRam += ramDelta;

    return { totalCpu, totalRam, details, totalPvc: 0 };
}

export function processAICenterProduct(product : ProductOptionInterface, details : Array<string>, totalCpu : number, totalRam : number, totalPvc : number, context?: ProductHandlerContext) {
    if (product.properties === undefined) {
        return ;
    }

    // Skills/pipelines scaling is RELATIVE to the calibrated baseline load for
    // this profile — the baseline load is already accounted for in AI Center's
    // base service resources, so we only add the delta above it (clamped >= 0).
    const baselineProfile = context?.baselineProfile || 'default';

    let cpuDelta = 0;
    let ramDelta = 0;
    let numberOfSkills = getNumericValue(product.properties["numberOfSkills"]);
    let skillsDelta = getBaselineExcess(product.key, baselineProfile, "numberOfSkills", numberOfSkills);

    if (skillsDelta > 0) {
        cpuDelta = 1000 * skillsDelta;
        ramDelta = 4 * 1024 * skillsDelta;
        details.push(additionalRequirements("Skills", numberOfSkills, cpuDelta, ramDelta));
        totalCpu += cpuDelta;
        totalRam += ramDelta;
    }

    let numberOfPipelines = getNumericValue(product.properties["numberOfPipelines"]);
    let pipelinesDelta = getBaselineExcess(product.key, baselineProfile, "numberOfPipelines", numberOfPipelines);
    if (pipelinesDelta > 0) {
        let propName = "Concurrent Pipelines";
        cpuDelta = 1000 * pipelinesDelta;
        ramDelta = 12 * 1024 * pipelinesDelta;
        details.push(additionalRequirements(propName, numberOfPipelines, cpuDelta, ramDelta));
        totalCpu += cpuDelta;
        totalRam += ramDelta;
        // PVC is EXCESS-only, like the CPU/RAM scaling above: 105 GiB per pipeline
        // above the calibrated baseline. The baseline pipelines' disk is part of
        // the documented reference sizing, not the calculator delta.
        const pvcDelta = 105 * pipelinesDelta;
        totalPvc += pvcDelta;
        details.push(`* The **${pipelinesDelta}** Concurrent Pipelines above the calibrated baseline require an additional dedicated **${pvcDelta}** GiB PVC (105 GiB each). This dedicated disk for AI center should be on the node where the training pipelines are running.`);
    }

    return { totalCpu, totalRam, totalPvc, details };
}


// `products` must be the EFFECTIVE products (floored/pinned scaling values) —
// dedicated-node sizing must match what the widgets display, not raw stored values.
export function calculateAsRobotNode(products: ProductOptionInterface[], isAutomationSuiteRobotsEnabled: boolean) {
    let asRobotNodeCpu = 0;
    let asRobotNodeRam = 0;
    let asRobotNodeDisk = 10;
    let isAdditionalNodeNeededEval = false;
    if (isAutomationSuiteRobotsEnabled) {
        let asRobotProduct = products.filter(p => p.key == constants.AUTOMATION_SUITE_ROBOTS_PRODUCT_OPTION.key)[0];
        if (asRobotProduct.properties !== undefined) {
            asRobotNodeCpu += asRobotProduct.properties["concurrentSmallRobots"] * 500;
            asRobotNodeCpu += asRobotProduct.properties["concurrentStandardRobots"] * 1000;
            asRobotNodeCpu += asRobotProduct.properties["concurrentMediumRobots"] * 2000;
            asRobotNodeCpu += asRobotProduct.properties["concurrentLargeRobots"] * 6000;
            asRobotNodeRam += asRobotProduct.properties["concurrentSmallRobots"] * 1 * 1024 * 1024 * 1024;
            asRobotNodeRam += asRobotProduct.properties["concurrentStandardRobots"] * 2 * 1024 * 1024 * 1024;
            asRobotNodeRam += asRobotProduct.properties["concurrentMediumRobots"] * 4 * 1024 * 1024 * 1024;
            asRobotNodeRam += asRobotProduct.properties["concurrentLargeRobots"] * 10 * 1024 * 1024 * 1024;
            asRobotNodeDisk += asRobotProduct.properties["concurrentSmallRobots"] * 1;
            asRobotNodeDisk += asRobotProduct.properties["concurrentStandardRobots"] * 2;
            asRobotNodeDisk += asRobotProduct.properties["concurrentMediumRobots"] * 4;
            asRobotNodeDisk += asRobotProduct.properties["concurrentLargeRobots"] * 10;
            isAdditionalNodeNeededEval = (asRobotProduct.properties["concurrentSmallRobots"] > 5 ||
                asRobotProduct.properties["concurrentStandardRobots"] > 0 ||
                asRobotProduct.properties["concurrentMediumRobots"] > 0 ||
                asRobotProduct.properties["concurrentLargeRobots"] > 0 )
        }
    }

    return { asRobotNodeCpu, asRobotNodeRam, asRobotNodeDisk, isAdditionalNodeNeededEval };
}

// `products` must be the EFFECTIVE products (see calculateAsRobotNode).
export function calculateStudioWebNode(products: ProductOptionInterface[], isStudioWebEnabled: boolean) {
    let studioWebNodeCpu = 0;
    let studioWebNodeRam = 0;
    let studioWebNodeDisk = 0;
    if (isStudioWebEnabled) {
        let studioWebProduct = products.filter(p => p.key == constants.STUDIO_WEB_PRODUCT_OPTION.key)[0];
        if (studioWebProduct.properties !== undefined) {
            const concurrentWorkflows = +(studioWebProduct.properties["concurrentWorkflows"] || 0);
            studioWebNodeCpu += concurrentWorkflows * 2000;  // 2 vCPU per workflow in millicores
            studioWebNodeRam += concurrentWorkflows * 4 * 1024 * 1024 * 1024;  // 4 GB per workflow
            studioWebNodeDisk += concurrentWorkflows * 4;  // 4 GiB per workflow
        }
    }

    return { studioWebNodeCpu, studioWebNodeRam, studioWebNodeDisk };
}

// `products` must be the EFFECTIVE products (see calculateAsRobotNode).
export function handleDocumentUnderstanding(products: ProductOptionInterface[]): {duGpuCount: number, ocrGpuRequired: boolean, modernExperienceGpuInfo : Map<string, number>} {
    let duGpuCount = 0;
    let duProducts = products.filter(p =>
        p.key == constants.DOCUMENT_UNDERSTANDING_AI_CENTER_PRODUCT_OPTION.key
        || p.key == constants.DOCUMENT_UNDERSTANDING_MODERN_PROJECTS_PRODUCT_OPTION.key
    );
    let ocrGpuRequired = false;
    let modernExperienceGpuInfo = new Map<string, number>();
    for (const duProduct of duProducts) {
        if (duProduct.properties === undefined) continue;

        const ocrPerHour = +duProduct.properties["ocrPerHour"];
        if (ocrPerHour > 60) {
            duGpuCount += 1;
            ocrGpuRequired = true;
        }

        const isModernProjectsEnabled = duProduct.key === constants.DOCUMENT_UNDERSTANDING_MODERN_PROJECTS_PRODUCT_OPTION.key
            || !!duProduct.properties["isModernProjectsEnabled"];
        if (isModernProjectsEnabled) {
            let customModelPagesPerHour = getNumericValue(duProduct.properties["customModelPagesPerHour"]);
            let oobModelPagesPerHour = getNumericValue(duProduct.properties["oobModelPagesPerHour"]);
            let modelsTrainingInParallel = getNumericValue(duProduct.properties["modelsTrainingInParallel"]);
            const cpuInference = !!duProduct.properties["cpuInference"];
            modernExperienceGpuInfo.set(constants.OCR_REPLICAS, Math.max(1, Math.ceil((customModelPagesPerHour + oobModelPagesPerHour) / DU_OCR_PAGES_PER_HOUR_PER_POD)));
            modernExperienceGpuInfo.set(constants.CUSTOM_MODEL_TRAINING_REPLICAS, Math.max(1, modelsTrainingInParallel));
            
            // custom model and OOB model run on GPU only if cpu inference is not enabled.
            if (!cpuInference) {
                modernExperienceGpuInfo.set(constants.CUSTOM_MODEL_REPLICAS, Math.max(1, Math.ceil(customModelPagesPerHour / DU_CUSTOM_EXTRACTOR_PAGES_PER_HOUR_GPU_PER_POD + modelsTrainingInParallel)));
                modernExperienceGpuInfo.set(constants.OOB_MODEL_REPLICAS, Math.max(1, Math.ceil(oobModelPagesPerHour / DU_OOB_EXTRACTOR_PAGES_PER_HOUR_GPU_PER_POD)));
            }
        }
    }
    return {duGpuCount, ocrGpuRequired, modernExperienceGpuInfo};
}


function getNumericValue(value: any): number {
    if (value === undefined) {
        return 0;
    }
    return +value;
}

export function processLLMGatewayProduct(product: ProductOptionInterface, details: Array<string>, totalCpu: number, totalRam: number, totalPvc: number, context?: ProductHandlerContext) {
    if (product.properties === undefined) {
        return;
    }
    const profile = context?.profile;
    const resolvedServices = context?.services;

    let concurrentRequests = getNumericValue(product.properties["concurrentRequests"]);
    let promptTokenSize = getNumericValue(product.properties["promptTokenSize"]);
    
    // LLM Gateway: 1000m CPU, 1024Mi RAM per replica
    // CPU Limits: 3000m per replica, Memory Limits: 3Gi per replica
    // HPA configuration:
    // - Evaluation/Lite: single replica (no HPA) = 1000m CPU, 1024Mi RAM
    // - Production/AS AIR: 2 replicas minimum (HPA Min/Max: 2/6) = 2000m CPU, 2048Mi RAM
    // Scaling parameters: concurrent requests, prompt token size
    
    const isEvaluationOrLite = profile === Profile.EvaluationOnly || profile === Profile.Lite;
    
    // Calculate additional CPU/RAM based on scaling parameters
    let cpuDelta = 0;
    let ramDelta = 0;
    
    if (concurrentRequests > 0) {
        const additionalReplicas = Math.max(0, Math.ceil((concurrentRequests - 10) / 20));
        cpuDelta = additionalReplicas * 1000;
        ramDelta = additionalReplicas * 1024;
    }
    
    if (promptTokenSize > 0 && concurrentRequests > 0) {
        const tokenMemoryMB = (promptTokenSize / 1000) * 1;
        ramDelta += Math.ceil(tokenMemoryMB * concurrentRequests / 100);
    }
    
    if (cpuDelta > 0 || ramDelta > 0) {
        details.push(additionalRequirements("Concurrent Requests", concurrentRequests, cpuDelta, ramDelta));
    }
    
    // LLM Gateway requirements (only if llmgateway service is resolved)
    if (!resolvedServices || resolvedServices.includes('llmgateway')) {
        const sqlMinSizeGB = 50;
        details.push(`* **LLM Gateway** requires SQL database (SQL Server >= 2019). Minimum size: **${sqlMinSizeGB}GB**.`);
        const redisMinSizeGB = 2.5;
        details.push(`* **LLM Gateway** requires Redis cache. Minimum size: **${redisMinSizeGB}GB** (memory).`);
    }

    // LLM Observability requirements (only if llmobservability service is resolved)
    if (!resolvedServices || resolvedServices.includes('llmobservability')) {
        const obsStoreSqlMinSizeGB = 50;
        details.push(`* **LLM Observability** requires SQL database (SQL Server >= 2019). Minimum size: **${obsStoreSqlMinSizeGB}GB**.`);
        const objectStoreMinSizeGB = 50;
        details.push(`* **LLM Observability** requires Object Store. Minimum size: **${objectStoreMinSizeGB}GB**.`);
    }

    // Context Grounding requirements (only if ecs service is resolved)
    if (!resolvedServices || resolvedServices.includes('ecs')) {
        let pagesPerMinute = getNumericValue(product.properties["pagesPerMinute"]);

        // Context Grounding consists of 4 components:
        // 1. dotnet: 600m CPU, 1Gi RAM
        // 2. python: 1000m CPU, 1Gi RAM
        // 3. frontend: 150m CPU, 100Mi RAM
        // 4. gotenberg: 500m CPU, 1Gi RAM
        // Total base per pod set: 2250m CPU, 3172Mi RAM

        const isEvaluationOrLite = profile === Profile.EvaluationOnly || profile === Profile.Lite;

        const baseCpuPerPodSet = 2250;
        const baseRamPerPodSet = 3172;

        let cgCpuDelta = 0;
        let cgRamDelta = 0;

        if (pagesPerMinute <= 25) {
            // 1 pod set (base)
        } else if (pagesPerMinute <= 50) {
            if (isEvaluationOrLite) {
                cgCpuDelta = baseCpuPerPodSet;
                cgRamDelta = baseRamPerPodSet;
            }
        } else if (pagesPerMinute <= 100) {
            if (isEvaluationOrLite) {
                cgCpuDelta = baseCpuPerPodSet * 3;
                cgRamDelta = baseRamPerPodSet * 3;
            } else {
                cgCpuDelta = baseCpuPerPodSet * 2;
                cgRamDelta = baseRamPerPodSet * 2;
            }
        } else {
            const podCount = Math.ceil(pagesPerMinute / 25);
            const basePodSets = isEvaluationOrLite ? 1 : 2;
            const additionalPodSets = podCount - basePodSets;
            if (additionalPodSets > 0) {
                cgCpuDelta = baseCpuPerPodSet * additionalPodSets;
                cgRamDelta = baseRamPerPodSet * additionalPodSets;
            }
        }

        if (pagesPerMinute > 0 && (cgCpuDelta > 0 || cgRamDelta > 0)) {
            details.push(additionalRequirements("Pages Per Minute", pagesPerMinute, cgCpuDelta, cgRamDelta));
        }

        cpuDelta += cgCpuDelta;
        ramDelta += cgRamDelta;

        const cgSqlMinSizeGB = 50;
        details.push(`* **Context Grounding** requires SQL database. Minimum size: **${cgSqlMinSizeGB}GB**.`);

        const cgObjectStoreMinSizeGB = 50;
        details.push(`* **Context Grounding** requires Object Store. Minimum size: **${cgObjectStoreMinSizeGB}GB**.`);

        const pvcMinSizeGB = 1024;
        details.push(`* **Context Grounding** requires PVC (Temporary storage). Minimum size: **${pvcMinSizeGB}GiB (1Ti)**.`);
        totalPvc += pvcMinSizeGB;

        details.push(`* **Context Grounding** requires access to **text-embeddings-3-large** or **gemini-embedding-001** and **gemini-2.5-flash** LLM models for fully functionality. Please refer to the product documentation for updates on the latest model requirement.`);
    }

    totalCpu += cpuDelta;
    totalRam += ramDelta;

    return { totalCpu, totalRam, details, totalPvc };
}

export function processA4EProduct(product: ProductOptionInterface, details: Array<string>, totalCpu: number, totalRam: number, totalPvc: number, context?: ProductHandlerContext) {
    if (product.properties === undefined) {
        return;
    }

    // Autopilot for Everyone (A4E) - A4E Cloud component
    // 300m CPU, 512Mi RAM per replica
    // CPU Limits: 750m per replica, Memory Limits: 2Gi per replica
    // HPA configuration:
    // - Evaluation/Lite: single replica (no HPA) = 300m CPU, 512Mi RAM
    // - Production/AS AIR: 2 replicas minimum (HPA Min/Max: 2/4) = 600m CPU, 1024Mi RAM
    // No scaling parameters - static resource requirements
    // No additional storage requirements (No PVC, No Object Store, No SQL, No Redis)
    
    // LLM Model requirements
    details.push(`* **Autopilot for Everyone** requires access to **gpt-4o-mini** or **gemini-2.5-flash** and **anthropic.claude-3-7-sonnet** LLM models for fully functionality. Please refer to the product documentation for updates on the latest model requirement.`);

    return { totalCpu, totalRam, details, totalPvc };
}

export function processAutomationSolutionsProduct(product: ProductOptionInterface, details: Array<string>, totalCpu: number, totalRam: number, totalPvc: number, context?: ProductHandlerContext) {
    if (product.properties === undefined) {
        return;
    }
    const profile = context?.profile;

    // Automation Solutions: 300m CPU, 1Gi RAM per replica
    // CPU Limits: 1000m per replica, Memory Limits: 2Gi per replica
    // HPA configuration:
    // - Evaluation/Lite: single replica (no HPA) = 300m CPU, 1Gi RAM
    // - Production/AS AIR: 2 replicas minimum (HPA Min/Max: 2/8) = 600m CPU, 2Gi RAM
    // No scaling parameters - static resource requirements

    const isEvaluationOrLite = profile === Profile.EvaluationOnly || profile === Profile.Lite;

    // SQL Database requirement
    const sqlMinSizeGB = 50;
    details.push(`* **Automation Solutions** requires SQL database. Minimum size: **${sqlMinSizeGB}GB**.`);

    // Object Store requirement
    const objectStoreMinSizeGB = 10;
    if (!isEvaluationOrLite) {
        details.push(`* **Automation Solutions** requires Object Store. Minimum size: **${objectStoreMinSizeGB}GB**, recommended: **50GB**.`);
    } else {
        details.push(`* **Automation Solutions** requires Object Store. Minimum size: **${objectStoreMinSizeGB}GB**.`);
    }

    // Redis requirement
    const redisMinSizeGB = 1;
    details.push(`* **Automation Solutions** requires Redis cache. Minimum size: **${redisMinSizeGB}GB** (memory).`);

    return { totalCpu, totalRam, details, totalPvc };
}

export function processAgentHubProduct(product: ProductOptionInterface, details: Array<string>, totalCpu: number, totalRam: number, totalPvc: number, context?: ProductHandlerContext) {
    if (product.properties === undefined) {
        return;
    }
    // SQL Database requirement
    const sqlMinSizeGB = 50;
    details.push(`* **Agent Hub** requires SQL database. Minimum size: **${sqlMinSizeGB}GB**.`);

    // Redis requirement
    const redisMinSizeGB = 1;
    details.push(`* **Agent Hub** requires Redis cache. Minimum size: **${redisMinSizeGB}GB** (memory).`);

    return { totalCpu, totalRam, details, totalPvc };
}

// ---------- Maestro PostgreSQL sizing ----------
//
// Formulas and thresholds come from the Maestro AS Architecture page
// (https://uipath.atlassian.net/wiki/spaces/POR/pages/90379813336), validated
// against the AKS Maestro/TaaS load test
// (https://uipath.atlassian.net/wiki/spaces/~605b29c08efc9b00704b1819/pages/90583138444).
//
// Complexity constants are redefined from the Architecture page to match realistic
// production workload shapes:
//   - Simple   =   3 BPMN tasks                                   →  25 activities/wf, 115 writes/wf, 150 KB/wf
//   - Moderate =  25 BPMN tasks + 1 subprocess (5 tasks) =  30    → 160 activities/wf, ~750 writes/wf, 800 KB/wf
//   - Complex  = 100 BPMN tasks + 3 subprocesses (5 tasks each) = 115 → 585 activities/wf, ~2730 writes/wf, 2800 KB/wf
//
// Activities-per-workflow formula (from the load test's extrapolation):
//   activities = 5 × total_bpmn_tasks + 10 baseline
//   (5 engine-internal activities per task; 10 baseline for ProcessOrchestrationRuntime + Cleanup, once per job)
// DB writes per workflow = activities × ~4.67 (14 state transitions/activity × 1/3 batching).
export const MAESTRO_COMPLEXITY = {
    simple:   { writes: 115,  storageKb: 150  },
    moderate: { writes: 750,  storageKb: 800  },
    complex:  { writes: 2730, storageKb: 2800 },
} as const;

// Postgres overhead multiplier: indexes, WAL, MVCC bloat, TOAST.
export const MAESTRO_PG_OVERHEAD = 2.5;
// Disk fill target: headroom for VACUUM and temporary spikes.
export const MAESTRO_DISK_FILL = 0.8;

// peak_writes_per_sec = Σ (wf_per_day × writes_per_wf) / 28800
//   Derived from the Architecture page formula; DURATION cancels out so it doesn't appear here.
//   28800 = 1440 min/day × 60 s/min / 3 (peak concurrency safety factor).
export function calculateMaestroPeakWritesPerSec(
    simpleWfPerDay: number,
    moderateWfPerDay: number,
    complexWfPerDay: number,
): number {
    const s = simpleWfPerDay   * MAESTRO_COMPLEXITY.simple.writes;
    const m = moderateWfPerDay * MAESTRO_COMPLEXITY.moderate.writes;
    const c = complexWfPerDay  * MAESTRO_COMPLEXITY.complex.writes;
    return (s + m + c) / 28800;
}

// steady_state_gb = Σ (wf_per_day × storage_kb) × pg_overhead × retention_days / 1e6
export function calculateMaestroSteadyStateGb(
    simpleWfPerDay: number,
    moderateWfPerDay: number,
    complexWfPerDay: number,
    retentionDays: number,
): number {
    const s = simpleWfPerDay   * MAESTRO_COMPLEXITY.simple.storageKb;
    const m = moderateWfPerDay * MAESTRO_COMPLEXITY.moderate.storageKb;
    const c = complexWfPerDay  * MAESTRO_COMPLEXITY.complex.storageKb;
    return (s + m + c) * MAESTRO_PG_OVERHEAD * retentionDays / 1e6;
}

// CPU/RAM from peak_writes_per_sec threshold lookup.
//
// Two upper tiers (4/8 and 8/16) come directly from the AKS load test's validated
// sustainable ceilings (~460 writes/sec on 4 CPU / 8 GiB, ~860 writes/sec on 8 CPU / 16 GiB).
// Postgres CPU was the dominant bottleneck in every test; memory stayed well under request
// (59% at 4 CPU, 34% at 8 CPU) — so we keep RAM generous at every tier.
//
// The < 50 bucket keeps a 2 vCPU / 4 GB floor for empty/tiny workloads only; anything
// larger gets bumped to the 4 CPU floor since the Architecture page's mid-tier 2 CPU
// buckets were never load-tested and CPU is generally the first ceiling hit.
export function lookupMaestroCpuRam(peakWritesPerSec: number): { cpu: number; ram: number } {
    if (peakWritesPerSec < 50)   return { cpu: 2, ram: 4  };
    if (peakWritesPerSec <= 500) return { cpu: 4, ram: 8  };
    return                              { cpu: 8, ram: 16 };
}

// Disk and IOPS from target_disk (steady_state / 0.8) threshold lookup.
// Architecture page lookup: 100 → 500 IOPS, 150 → 800, 250 → 1110, 500 → 2300, 1000 → 5000.
// Above 1 TB: round up to nearest 0.5 TB, keep 5000 IOPS.
export function lookupMaestroDisk(targetDiskGb: number): { diskGb: number; iops: number } {
    if (targetDiskGb <= 100)  return { diskGb: 100,  iops: 500  };
    if (targetDiskGb <= 150)  return { diskGb: 150,  iops: 800  };
    if (targetDiskGb <= 250)  return { diskGb: 250,  iops: 1110 };
    if (targetDiskGb <= 500)  return { diskGb: 500,  iops: 2300 };
    if (targetDiskGb <= 1000) return { diskGb: 1000, iops: 5000 };
    const diskGb = Math.ceil(targetDiskGb / 500) * 500;
    return { diskGb, iops: 5000 };
}

// Which high-level profile category a Profile enum value falls under.
type MaestroProfileTier = 'default' | 'lite' | 'ha';

function classifyMaestroProfile(profile: Profile): MaestroProfileTier {
    switch (profile) {
        case Profile.Lite:
        case Profile.AKSLite:
        case Profile.EKSLite:
        case Profile.OpenshiftLite:
            return 'lite';
        case Profile.HAProduction:
        case Profile.AKSHA:
        case Profile.EKSHA:
        case Profile.OpenshiftHA:
            return 'ha';
        default:
            return 'default';
    }
}

// Postgres connections from profile AND workload. Default is flat (HPA off). Lite and HA
// scale with peak_writes_per_sec using the same tier boundaries as the CPU/RAM lookup
// (since HPA scales pods by CPU/memory utilization and each pod contributes a fixed
// connection share).
//
//  Peak writes/sec     Default   Lite    HA
//   < 50                 200      200   400
//   50–500               200      400   500
//   > 500                200      600   600
export function lookupMaestroConnections(profile: Profile, peakWritesPerSec: number): number {
    const tier = classifyMaestroProfile(profile);
    if (tier === 'default') return 200;
    if (peakWritesPerSec < 50) {
        return tier === 'lite' ? 200 : 400;
    }
    if (peakWritesPerSec <= 500) {
        return tier === 'lite' ? 400 : 500;
    }
    return 600;
}

export type MaestroPgContribution = {
    cpu: number;          // vCPU
    ram: number;          // GiB
    disk: number;         // GiB
    iops: number;
    connections: number;
};

// Full Maestro PostgreSQL sizing from product properties + deployment profile.
export function calculateMaestroPgContribution(
    product: ProductOptionInterface,
    profile: Profile,
): MaestroPgContribution {
    const simpleWf   = getNumericValue(product.properties?.simpleWfPerDay);
    const moderateWf = getNumericValue(product.properties?.moderateWfPerDay);
    const complexWf  = getNumericValue(product.properties?.complexWfPerDay);
    const retentionRaw = getNumericValue(product.properties?.retentionDays);
    const retentionDays = retentionRaw > 0 ? retentionRaw : 30;

    const peakWritesPerSec = calculateMaestroPeakWritesPerSec(simpleWf, moderateWf, complexWf);
    const steadyStateGb = calculateMaestroSteadyStateGb(simpleWf, moderateWf, complexWf, retentionDays);
    const targetDiskGb = steadyStateGb / MAESTRO_DISK_FILL;

    const { cpu, ram } = lookupMaestroCpuRam(peakWritesPerSec);
    const { diskGb, iops } = lookupMaestroDisk(targetDiskGb);
    const connections = lookupMaestroConnections(profile, peakWritesPerSec);

    return { cpu, ram, disk: diskGb, iops, connections };
}

// Product handler for Maestro. The shared-PostgreSQL-node aggregation in
// calculationUtils pre-computes Maestro's contribution from calculateMaestroPgContribution
// and patches it into PG_CONTRIBUTIONS before the per-product section renders. This handler
// is registered for completeness (and to match the dispatch signature) but has nothing extra
// to emit in the per-service section today.
export function processMaestroProduct(
    product: ProductOptionInterface,
    details: Array<string>,
    totalCpu: number,
    totalRam: number,
    totalPvc: number,
    context?: ProductHandlerContext,
) {
    if (product.properties === undefined) {
        return;
    }
    return { totalCpu, totalRam, details, totalPvc };
}

